import { Injectable } from '@angular/core';

/** Un singolo sfondo personalizzato (immagine come data-URL già ridimensionato lato client). */
export interface BrandingSlot {
	image: string;
	size: 'cover' | 'contain' | 'repeat';
	posX: number; // 0-100 (%)
	posY: number; // 0-100 (%)
	overlay: number; // 0-60 (%) — scurimento (header) o velatura chiara (body)
	menu?: boolean; // solo header: applica l'immagine anche al menu (sidebar/topnav)
}

/** Sfondi personalizzati dell'azienda (colonna cmp_companies.cmp_branding, JSON). */
export interface CompanyBranding {
	header?: BrandingSlot | null;
	body?: BrandingSlot | null;
	/** Colore di sfondo dell'header (barra superiore). Vuoto/nullo = colore del tema. */
	headerColor?: string | null;
	/** Colore testo header/menu (voci menu, icone, nome utente). Vuoto/nullo = colore del tema. */
	headerText?: string | null;
	/** Colore testo header/menu in hover. Vuoto/nullo = colore del tema. */
	headerTextHover?: string | null;
}

/**
 * Applica gli sfondi personalizzati per azienda (tab Impostazioni anagrafica) via CSS variables
 * --kcs-bgc-* sul root (stesso pattern di kcs-theme). Il valore persistito arriva dal backend
 * (companies/branding) via AuthGuard; qui viene cacheato in localStorage per riapplicarlo
 * subito al load successivo senza flash.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
	constructor() {
		let cached: CompanyBranding = {};
		try {
			cached = JSON.parse(localStorage.getItem('branding') || '{}');
		} catch {
			cached = {};
		}
		this.setVars(cached);
	}

	apply(branding: CompanyBranding | null | undefined): void {
		const value = branding || {};
		try {
			localStorage.setItem('branding', JSON.stringify(value));
		} catch {
			// quota localStorage superata: si perde solo la cache, non l'applicazione corrente
		}
		this.setVars(value);
	}

	/**
	 * Rimuove tutte le variabili --kcs-* inline dal root e la cache localStorage.
	 * Da chiamare al logout: le variabili inline su <html> sopravvivono alla
	 * navigazione SPA, altrimenti l'azienda successiva erediterebbe colori e
	 * sfondi della precedente (anche sulla pagina di login).
	 */
	clear(): void {
		try {
			localStorage.removeItem('branding');
		} catch {
			// ignore
		}
		this.setVars({});
	}

	private setVars(branding: CompanyBranding): void {
		this.setSlot('header', branding.header);
		// il menu riusa l'immagine dell'header (con velatura fissa del colore tema, vedi styles.css)
		this.setSlot('menu', branding.header && branding.header.menu !== false ? branding.header : null);
		this.setSlot('body', branding.body);
		this.setHeaderColor(branding.headerColor);
		this.setHeaderTextColors(branding.headerText, branding.headerTextHover);
	}

	/** Colori testo header/menu: impostati inline sul root vincono sulle variabili del tema. */
	private setHeaderTextColors(text?: string | null, hover?: string | null): void {
		const root = document.documentElement.style;
		const set = (name: string, value?: string | null) => (value ? root.setProperty(name, value) : root.removeProperty(name));
		set('--kcs-header-text', text);
		set('--kcs-topnav-text', text);
		set('--kcs-sidebar-nav-text', text);
		set('--kcs-topnav-text-hover', hover);
		set('--kcs-sidebar-nav-text-hover', hover);
	}

	/** Colore di sfondo dell'header: variabile dedicata usata da .layout-topbar (fallback al tema). */
	private setHeaderColor(color?: string | null): void {
		const root = document.documentElement.style;
		if (color) {
			root.setProperty('--kcs-header-bg', color);
		} else {
			root.removeProperty('--kcs-header-bg');
		}
	}

	private setSlot(name: string, slot?: BrandingSlot | null): void {
		const rootEl = document.documentElement;
		const root = rootEl.style;
		const prefix = `--kcs-bgc-${name}`;
		const image = this.sanitizeImage(slot?.image);
		if (slot && image) {
			root.setProperty(`${prefix}-image`, `url("${image}")`);
			root.setProperty(`${prefix}-size`, slot.size === 'repeat' ? 'auto' : slot.size || 'cover');
			root.setProperty(`${prefix}-repeat`, slot.size === 'repeat' ? 'repeat' : 'no-repeat');
			root.setProperty(`${prefix}-position`, `${slot.posX ?? 50}% ${slot.posY ?? 50}%`);
			root.setProperty(`${prefix}-overlay`, String((slot.overlay ?? 0) / 100));
			// L'attributo abilita la regola CSS relativa (es. la velatura sidebar),
			// che altrimenti azzererebbe lo sfondo del tema quando non c'è immagine.
			rootEl.setAttribute(`kcs-bgc-${name}`, '');
		} else {
			for (const suffix of ['image', 'size', 'repeat', 'position', 'overlay']) {
				root.removeProperty(`${prefix}-${suffix}`);
			}
			rootEl.removeAttribute(`kcs-bgc-${name}`);
		}
	}

	/**
	 * Ammette solo data-URL di immagini: il valore arriva dal JSON cmp_branding
	 * (potenzialmente manomesso via API) e finisce in url("...") applicato a tutti
	 * gli utenti dell'azienda; un URL esterno diventerebbe un beacon di tracciamento.
	 */
	private sanitizeImage(image?: string | null): string {
		return image && /^data:image\//i.test(image.trim()) ? image.trim() : '';
	}
}
